import { useCallback } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Paths } from "src/paths"

/*
    Monitor's deep link. Three states:

      /engagements/:id/monitor                    the campaign table
      /engagements/:id/monitor/:launchId          one campaign's dashboard
      /engagements/:id/monitor/objective/:id      one objective's dashboard

    Monitor has its own hook rather than sharing useItemRoute because it drills
    into two different kinds of record. useItemRoute returns a bare Number(itemId)
    with no type, and objective ids and launch ids come from different tables that
    both count from 1 — /monitor/3 would be ambiguous. The literal "objective"
    segment makes the kind explicit in the URL.

    An audience narrows a campaign rather than being a thing you drill into, so it
    rides along as ?audience= instead of taking a path segment. It is set once, by
    the audience row in the table — the dashboard has no picker, so there is
    nothing here to change it in place.
*/

export type MonitorScope =
  | { kind: "table" }
  | { kind: "campaign"; launchId: number; audienceId?: number }
  | { kind: "objective"; objectiveId: number }

const OBJECTIVE_SEGMENT = "objective"

export function useMonitorRoute() {
  const { engagementId, tab, itemId, subId } = useParams<{
    engagementId: string
    tab: string
    itemId: string
    subId: string
  }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const base = `${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}/${engagementId}/${tab}`

  const scope = parseScope(itemId, subId, searchParams.get("audience"))

  const openTable = useCallback(() => navigate(base), [base, navigate])

  const openCampaign = useCallback(
    (launchId: number) => navigate(`${base}/${launchId}`),
    [base, navigate],
  )

  const openObjective = useCallback(
    (objectiveId: number) => navigate(`${base}/${OBJECTIVE_SEGMENT}/${objectiveId}`),
    [base, navigate],
  )

  // Straight from an audience row in the table to that audience's charts. One
  // navigate carrying the filter, rather than opening the campaign and then
  // setting a param — which would leave the unfiltered view in history.
  const openCampaignAudience = useCallback(
    (launchId: number, audienceId: number) =>
      navigate(`${base}/${launchId}?audience=${audienceId}`),
    [base, navigate],
  )

  return { scope, openTable, openCampaign, openObjective, openCampaignAudience }
}

function parseScope(
  itemId: string | undefined,
  subId: string | undefined,
  audience: string | null,
): MonitorScope {
  if (itemId === OBJECTIVE_SEGMENT) {
    const objectiveId = Number(subId)
    return Number.isFinite(objectiveId) && objectiveId > 0
      ? { kind: "objective", objectiveId }
      : { kind: "table" }
  }

  const launchId = Number(itemId)
  if (!Number.isFinite(launchId) || launchId <= 0) {
    return { kind: "table" }
  }

  const audienceId = Number(audience)
  return {
    kind: "campaign",
    launchId,
    audienceId: Number.isFinite(audienceId) && audienceId > 0 ? audienceId : undefined,
  }
}
